import { AppUtils } from '../../shared/utils/index';
import { BasicApplicationRouter } from './BasicApplicationRouter';


/**
 * Handles API related error codes.
 */
export class BasicErrorService<T extends BasicAppDefinition> {
	protected readonly lookupServerFieldnames: { [key: string]: string };
	protected readonly router: BasicApplicationRouter<T>;
	protected validClass: string;
	protected errorClass: string;
	protected $ctx: JQuery;
	protected readonly options: BasicAppOptions<T>;
	constructor(options: BasicAppOptions<T>) {
		this.options = options;
		this.$ctx = $(options.$ctx);

		this.errorClass = '.js-result-error';
		this.validClass = '.js-result-valid';

		this.router = new BasicApplicationRouter(options);
		this.lookupServerFieldnames = {};
		this.fillLookupServerFieldnames();
	}

	/**
	 * handleError.
	 *
	 * event listener to handle the server side validation.
	 * It maps the error messages of the response object to the single/grouped fields
	 */
	public handleError(jqxhr?: JQueryXHR): void {
		const elems: JQuery[] = [];
		let offset = 0;

		AppUtils.View.stopLoader();

		if (this.handleServerErrorWithErrorPageRedirect(jqxhr)) {
			return;
		}
		if (jqxhr?.responseJSON?.Messages) {
			this.clearErrorMessages();
			const messages = jqxhr.responseJSON.Messages as AdacResultMessage[];
			messages.slice().reverse().forEach((item): void => {
				const source = this.getMappedSource(item.Source);

				if (this.isFieldError(item)) {
					this.handleFieldErrors(this.$ctx, source, item, elems);
				} else if (this.isGeneralError(item)) {
					this.handleGeneralError(item);
				}
			});

			offset = this.getErrorElementOffset(elems);
		} else {
			offset = 150;
		}
		AppUtils.View.scrollTo(offset - 150);
	}

	private getErrorElementOffset(elems: JQuery[]): number {
		let $elem = this.$ctx;
		let offset = $elem.offset()?.top ?? 0;

		if (elems.length > 0) {
			$elem = elems[0];
			let t = elems[0].offset()?.top ?? Number.MAX_SAFE_INTEGER;
			elems.forEach((elem) => {
				const top = elem.offset()?.top ?? Number.MAX_SAFE_INTEGER;
				if (top < t) {
					$elem = elem;
					t = top;
				}
			});
			offset = t;
		}

		if (offset < 1000) {
			offset = 200;
		} else if (offset > 1500) {
			offset = 500;
		}
		return offset;
	}

	protected clearErrorMessages(): void {
		this.$ctx.find('div.js-added-error-msg').remove();
	}

	protected handleFieldErrors($ctx: JQuery, source: string, item: AdacResultMessage, elems: JQuery[]): void {
		if (!item.Group) {
			const errorHtml = `<div class="ll-error-msg js-added-error-msg" data-field="${item.Source}" style="color: #cc3300;">${this.getErrorMessage(item)}</div>`;
			const $sourceElement = $ctx.find(`input[name="${source}"]`);
			$sourceElement.addClass('is-error');
			$sourceElement.parents('.ll-col-data').find('.ll-error-msg').remove();
			$sourceElement.parents('.js-row').removeClass('is-valid');
			$sourceElement.parents('.js-row').addClass('is-error');
			$sourceElement.parents('.ll-col-data').prepend(errorHtml);
			if ($sourceElement.length) {
				elems.push($sourceElement);
			} else {
				console.warn(`No element found to show validation message for '${source}'. Maybe an according mapping is missing in fillLookupServerFieldnames.`);
			}
		}
	}

	protected handleGeneralError(_item: AdacResultMessage): void {
		this.handleTechnicalErrorWithTemplate(undefined as unknown as JQueryXHR, true, (this.options.app.templates.error || this.options.app.templates.errorpage)?.templateurl);
	}

	/**
	 *
	 * @param jqxhr
	 * @param applicationName
	 * @param validationCb
	 * @param clearSessionCb
	 * @param renderSuccessTemplateCb forces the rendering of a success template, which is required, for example, for the insurances
	 */
	public handleDefaultErrorResponse(
		jqxhr: JQuery.jqXHR,
		applicationName: string,
		validationCb: () => void,
		clearSessionCb?: () => void,
		renderSuccessTemplateCb?: () => void
	): void {
		AppUtils.View.stopLoader();
		const appApiErrorName = `${applicationName} API error`;

		if (jqxhr?.status === 400) {
			if (this.isServerValidationError(jqxhr)) {
				validationCb();
			} else if (this.isQualifiedError(jqxhr)) {
				if (clearSessionCb) {
					clearSessionCb();
				}
				this.handleTechnicalError(jqxhr);
			} else if (renderSuccessTemplateCb) {
				this.hideErrorDialog();
				renderSuccessTemplateCb();
			}
		} else {
			this.handleAllOtherHttpErrors(appApiErrorName);
		}
	}

	/**
	 * Handles a technical error by passing the ajax error callback to the according Error-Method.
	 *
	 * @param jqxhr - the XMLHttpRequest object from the ajax error callback.
	 */
	public handleTechnicalError(jqxhr: JQueryXHR): void {
		AppUtils.Helper.logError(jqxhr, this.options.name, window.location.href, this.options.name);
		if (!this.handleServerErrorWithErrorPageRedirect(jqxhr)) {
			if (this.isQualifiedError(jqxhr)) {
				this.handleTechnicalErrorWithErrorQualifiedTemplate(jqxhr, true);
			} else {
				this.handleTechnicalErrorWithErrorTemplate(jqxhr, true);
			}
		}
	}

	/**
	 * Handles a technical error by showing the according error block (in the sticky box). Additionally performs logging.
	 *
	 * @param jqxhr - the XMLHttpRequest object from the ajax error callback.
	 * @param url - optional url to log instead of jqxhr.url.
	 */
	public handleTechnicalErrorWithErrorBlock(jqxhr: any, url?: string): void {
		this.$ctx.find(this.errorClass).show();
		this.$ctx.find(this.validClass).hide();
		// hint: accessing jqxhr.url here that is set in utils.ajax.js
		AppUtils.Helper.logError(jqxhr, this.options.name, window.location.href, url || jqxhr.url);
	}

	/**
	 * handle all other errors (for example 500er) except 400 from backend API requests
	 *
	 * @param subject - optional subject Product name.
	 */
	public handleAllOtherHttpErrors(subject: string): void {
		const errorMessages = this.options.app.validationmessages.backend['code_Anlage'] || "Unknown Error";
		this.errorDialog(AppUtils.Helper.linkify(errorMessages, subject));
	}

	/**
	 * Hides the error block and ensures the normal data is shown (in the sticky box).
	 */
	public clearTechnicalErrorWithErrorBlock(): void {
		this.$ctx.find(this.errorClass).hide();
		this.$ctx.find(this.validClass).show();
	}

	/**
	 * Handles a technical error by rendering the error template.
	 *
	 * @param jqxhr - the XMLHttpRequest object to extract the error codes from.
	 * @param supressErrorMessages - set to true to not show the server error messages but only the general error text.
	 */
	public handleTechnicalErrorWithErrorTemplate(jqxhr: JQueryXHR, supressErrorMessages: boolean): void {
		if (!this.options.app.templates.error) {
			AppUtils.Helper.logInfo("Missing template", this.options.app.applicationId, window.location.href, "Template 'error' not found");
		}
		this.handleTechnicalErrorWithTemplate(jqxhr, supressErrorMessages, this.options.app.templates.error?.templateurl);
	}

	/**
	 * Handles a technical error by rendering the qualified error template.
	 *
	 * @param jqxhr - the XMLHttpRequest object to extract the error codes from.
	 * @param supressErrorMessages - set to true to not show the server error messages but only the general error text.
	 */
	public handleTechnicalErrorWithErrorQualifiedTemplate(jqxhr: JQueryXHR, supressErrorMessages: boolean): void {
		if (!this.options.app.templates.errorqualified) {
			AppUtils.Helper.logInfo("Missing template", this.options.app.applicationId, window.location.href, "Template 'errorqualified' not found");
		}
		this.handleTechnicalErrorWithTemplate(jqxhr, supressErrorMessages, this.options.app.templates.errorqualified?.templateurl);
	}

	/**
	 * Handles a technical error by rendering the template at the provided url.
	 *
	 * @param jqxhr - the XMLHttpRequest object to extract the error codes from.
	 * @param supressErrorMessages - set to true to not show the server error messages but only the general error text.
	 * @param templateUrl - the url to the template to render.
	 */
	public handleTechnicalErrorWithTemplate(jqxhr: JQueryXHR, supressErrorMessages: boolean, templateUrl: string): void {
		if (templateUrl) {
			AppUtils.Ajax.fragment({
				url: templateUrl,
				dataType: 'html',
				timeout: 30000
			},
				(template: string) => {
					let errorMessages: string[] = [];
					if (!supressErrorMessages) {
						errorMessages = this.getErrorMessageFromJqxhr(jqxhr);
					}
					const viewOptions = {
						errorresponse: {
							Messages: errorMessages,
							prevurl: this.options.app.prevurl
						}
					};
					this.renderTemplate(template, viewOptions);
				}, () => {
					this.router.error();
				});
		} else {
			this.showGeneralErrorLayer();
		}
	}

	protected renderTemplate(template: string, config: { [key: string]: any }): void {
		AppUtils.View.renderTemplate(template, config, this.$ctx, function () { /* ignore */ });
	}

	/**
	 * Handles server errors (5xx) from backend API requests by redirecting to the error page defined for the application.
	 *
	 * @param jqxhr - the XMLHttpRequest object from the ajax error callback.
	 * @param url - optional url to log instead of jqxhr.url.
	 * @return true, if the error was a server error that could be handled, false otherwise.
	 */
	public handleServerErrorWithErrorPageRedirect(jqxhr?: JQueryXHR, url?: string): boolean {
		if (this.isFatalError(jqxhr)) {
			AppUtils.Helper.logError(jqxhr, this.options.name, window.location.href, url || (jqxhr as JQueryXHR & { url: string }).url);
			this.router.error();
			return true;
		}
		return false;
	}

	/**
	 * Maps response messages from backend API requests to according error messages from the application JSON and shows them in an error dialog.
	 *
	 * @param jqxhr - the XMLHttpRequest object from the ajax error callback.
	 * @param subject - optional subject for mailto links.
	 * @return true, if the error was a bad request error that could be handled, false otherwise.
	 */
	public handleBadRequestErrorWithDialog(jqxhr: JQueryXHR, subject: string): boolean {
		if (jqxhr.status === 400) {
			let errorMessages = '';
			const messages = jqxhr.responseJSON.Messages as AdacResultMessage[];
			messages.slice().reverse().forEach((item) => {
				errorMessages += `${this.getErrorMessage(item)}<br />`;
			});
			this.errorDialog(AppUtils.Helper.linkify(errorMessages, subject));
			return true;
		}
		return false;
	}

	/* istanbul ignore next: wrapper function */
	public showModal(element: JQuery | HTMLElement, message?: string): void {
		this.displayDialog(element, message, true);
	}

	/**
	 * Shows an error dialog / lightbox with the provided message.
	 * Needs an error_dialog div-block at the end of the template.
	 */
	public errorDialog(message: string, isModal = false): void {
		this.displayDialog($('#error_dialog'), message, isModal);
	}

	private displayDialog(element: JQuery | HTMLElement, message?: string, isModal = false): void {
		AppUtils.View.displayDialog(element, message, isModal);
		AppUtils.View.stopLoader();
	}

	/**
	 * hide error dialog or lightbox.
	 */
	public hideErrorDialog(): void {
		$.magnificPopup.close();
	}

	/**
	 * Checks the provided response json for the CancelledOnValidation flag.
	 * Currently there may be more than one CancelledOnValidation flag.
	 * If at least one CancelledOnValidation flag is true, it is treated as servervalidation error.
	 *
	 * @param jqxhr - the XMLHttpRequest object from the ajax error callback.
	 *
	 * @return true, if servervalidation error, false if non servervalidation error.
	 */
	public isServerValidationError(jqxhr: JQueryXHR): boolean {
		let result = false;
		if (jqxhr && jqxhr.responseJSON && jqxhr.responseJSON.Messages) {
			const messages = jqxhr.responseJSON.Messages as AdacResultMessage[];
			result = messages.some((item) => {
				if (item.CancelledOnValidation) {
					return true;
				}
				return false;
			});
		}
		return result;
	}

	/**
	 * Checks the provided response json for the stop flag.
	 * Currently there may be more than one stop flag.
	 * If at least one stop flag is true, it is treated as qualified error.
	 *
	 * @param jqxhr - the XMLHttpRequest object from the ajax error callback.
	 *
	 * @return true, if qualified error, false if non qualified error.
	 */
	public isQualifiedError(jqxhr: JQueryXHR): boolean {
		let result = false;
		if (jqxhr && jqxhr.responseJSON && jqxhr.responseJSON.Messages) {
			const messages = jqxhr.responseJSON.Messages as AdacResultMessage[];
			result = messages.some((item) => {
				if (item.Stop) {
					return true;
				}
				return false;
			});
		}
		return result;
	}

	public isFatalError(jqxhr?: JQueryXHR): boolean {
		return null != jqxhr?.status && jqxhr.status.toString().startsWith('5'); // NOSONAR
	}

	/**
	 * @return true if error is a field error
	 */
	protected isFieldError(error: AdacResultMessage): boolean {
		return !this.isGeneralError(error);
	}

	/**
	 * isGeneralError.
	 *
	 * @return true if error is a field error
	 */
	protected isGeneralError(error: AdacResultMessage): boolean {
		return this.options.app.generalErrorCodes.some((item) => {
			// eslint-disable-next-line eqeqeq
			return item.code == error.Id; //NOSONAR
		});
	}

	/**
	 * getErrorMessage.
	 *
	 * @return error message from cms
	 *
	 */
	public getErrorMessage(item: AdacResultMessage): string {
		let result = item.Message;
		if (this.options.app.validationmessages && this.options.app.validationmessages.backend[`code_${item.Id}`]) {
			result = this.options.app.validationmessages.backend[`code_${item.Id}`];
		}
		return result;
	}

	/**
	 * Extracts message codes from jqxhr and returns according error messages from CMS.
	 *
	 * @param jqxhr - the XMLHttpRequest object from the ajax error callback.
	 * @return array with error messages or empty array.
	 */
	public getErrorMessageFromJqxhr(jqxhr?: JQueryXHR): string[] {
		const errorMessages: string[] = [];
		if (jqxhr && jqxhr.responseJSON && jqxhr.responseJSON.Messages) {
			const messages = jqxhr.responseJSON.Messages as AdacResultMessage[];
			messages.slice().reverse().forEach((item) => {
				errorMessages.push(this.getErrorMessage(item));
			});
		}
		return errorMessages;
	}

	/***
	 * shows the genereal error lightbox with timestamp and referencenumber
	 */
	/* istanbul ignore next: wrapper function */
	public showGeneralErrorLayer(): void {
		T.Utils.View.showGeneralErrorLayer();
	}

	protected getMappedSource(source: string): string {
		let result = source.replace(/^(Anlage\.Antragssteller|BankUpdateMitAktion|Anlage\.Versicherungsnehmer|LangzeitAksAnlage|IncAnlage)./, '');

		// show error on checkbox not on field and checkbox
		result = result.replace('Anlage.Ausbildungsende', 'InAusbildung');

		if (this.lookupServerFieldnames[source]) {
			result = this.lookupServerFieldnames[source];
		} else if (source.indexOf("Kind") > -1) {
			result = this.getChildFieldSource(source);
		}
		return result;
	}

	/**
	 * Lookup map for Serverfieldnames and field ids
	 * key: serverfieldname
	 * value: field id
	 */
	protected fillLookupServerFieldnames(): void {
		this.lookupServerFieldnames["Anlage.Partner.Email"] = this.lookupServerFieldnames["MitgliedschaftAnlageErweiterung.Partner.Email"] = "EmailPartner";
		this.lookupServerFieldnames["Anlage.Partner.Geburtsdatum"] = this.lookupServerFieldnames["MitgliedschaftAnlageErweiterung.Partner.Geburtsdatum"] = "GeburtsdatumPartner";

		this.lookupServerFieldnames["MeinPostfachService:VerifyTokenAsync"] = "ConfirmationCode";
	}

	protected getChildFieldSource(source: string): string {
		const splitted = source.split(".");
		if (splitted.length) {
			let field = splitted[splitted.length - 1].toLowerCase();
			const childIndex = splitted[0].split("_")[1] || "0";

			//mapping needed
			if (field === "strassehausnummer") {
				field = "street";
			} else if (field === "geburtsdatum") {
				field = "birthday";
			} else if (field === "prefix") {
				field = "phonepre";
			}

			return `${field}_c_${childIndex}`;
		}
		return source;
	}

	public getErrorMessageFromId(jqxhr: JQueryXHR, errorId: number): AdacResultMessage {
		if (jqxhr && jqxhr.responseJSON && jqxhr.responseJSON.Messages) {
			const messages = jqxhr.responseJSON.Messages as AdacResultMessage[];
			return messages.find((item) => {
				return item.Id === errorId;
			});
		}
		return undefined;
	}

	public hasErrorWithId(jqxhr: JQueryXHR, errorId: number): boolean {
		if (jqxhr && jqxhr.responseJSON && jqxhr.responseJSON.Messages) {
			const messages = jqxhr.responseJSON.Messages as AdacResultMessage[];
			return messages.some((item) => {
				return item.Id === errorId;
			});
		}
		return false;
	}
}